<script setup lang="ts">
import { computed } from 'vue'

import type { DashboardWidget } from '../../models/widget.types'

import OverviewChartWidget from './OverviewChartWidget.vue'

import {
  isKeyEventChartField,
  mapColumns,
  scheduleTabDetails,
  useMetricCatalog,
  useReportSchedules,
  type SectionDataVisibleConfig,
} from '@/features/reports'

const props = withDefaults(
  defineProps<{
    config: SectionDataVisibleConfig
    defaultPostData: string
    defaultMetricFields?: readonly string[]
    excludeMetrics?: readonly string[]
    calcRoasFirstMetric?: string
    hideDatePicker?: boolean
    /**
     * v3 `dashboardConfig.keyEvents` (`OrphexDashboardChartArea.vue:183-187`). Narrows the
     * trendline's metric set to the partition's conversion-ish columns instead of every numeric
     * one — what makes a Key Events section's chart plot key events rather than cost and CPC.
     */
    keyEvents?: boolean
  }>(),
  {
    defaultMetricFields: () => [],
    excludeMetrics: () => [],
    keyEvents: false,
    calcRoasFirstMetric: '',
    hideDatePicker: false,
  },
)
/** Forwarded verbatim to `OverviewChartWidget` — see that component for why this slot exists. */
defineSlots<{ controls?: () => unknown }>()

const schedules = useReportSchedules()
const catalog = useMetricCatalog()

const scheduleDetails = computed(() =>
  scheduleTabDetails(
    schedules.checkScheduleTabs(props.config.reportId, props.config.scheduleTabs ?? []),
  ),
)
const metricFields = computed(() => {
  const excluded = new Set(props.excludeMetrics)
  const available = props.config.availableFields ?? props.config.metrics ?? []
  return mapColumns(available, {
    getColumn: catalog.getColumn,
    isAvailable: (column) => column.numeric === true && column.sub_type !== 'meta',
  })
    .map((column) => column.field)
    .filter((field) => !excluded.has(field) && (!props.keyEvents || isKeyEventChartField(field)))
})
/**
 * v3 `OrphexDashboardChartArea.getRequest`: the schedule tab's `highlighted_metrics` REPLACE the
 * section's `defaultMetricFields` (`attrSelected || selectedMetrics`) rather than merging with
 * them, so the chart opens on the cards the schedule flags. The list goes down whole — its length
 * is the initial card count (`OverviewChartWidget`) — but only once at least one of its fields is
 * plottable here; otherwise (e.g. key-event narrowing) the next source takes over.
 */
const highlightedMetricFields = computed(() => {
  const available = new Set(metricFields.value)
  const highlighted = [
    ...new Set(scheduleDetails.value.flatMap((tab) => tab.highlighted_metrics ?? [])),
  ]
  const defaults = [...new Set(props.defaultMetricFields)]
  const usable = (fields: string[]) => fields.some((field) => available.has(field))
  if (usable(highlighted)) return highlighted
  if (usable(defaults)) return defaults
  return metricFields.value.slice(0, 4)
})
const widget = computed<DashboardWidget>(() => ({
  kind: 'dashboard_chart',
  wire: {
    visibleType: 'dashboard_chart',
    id: props.config.id,
    reportId: props.config.reportId,
    ...(props.config.partitionName ? { partition: props.config.partitionName } : {}),
    ...(props.config.partitionOnly ? { partition_only: true } : {}),
    setMetrics: metricFields.value,
    defaultFilters: props.config.filters ?? [],
  },
}))
</script>

<template>
  <OverviewChartWidget
    :widget="widget"
    :default-post-data="defaultPostData"
    :default-metric-fields="highlightedMetricFields"
    :calc-roas-first-metric="calcRoasFirstMetric"
    :hide-date-picker="hideDatePicker"
  >
    <template v-if="$slots.controls" #controls><slot name="controls" /></template>
  </OverviewChartWidget>
</template>
